import { useEffect, useRef, useState } from "react";
import type { RiskPlusReport } from "../../entities/report/RiskPlusReport";

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from "chart.js";
import { Radar } from 'react-chartjs-2';


ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export interface IInternalRiskRadarProps {
  reportData: RiskPlusReport
}

export default function InternalRiskRadarChart({ reportData }: IInternalRiskRadarProps) {

  const chartRef = useRef<ChartJS<"radar"> | null>(null);
  const [chartImage, setChartImage] = useState<string | null>(null);

  // --- helpers ---
  const getRiskValue = (harmRank: string): number => {
    switch (harmRank.toLowerCase()) {
      case "critical":
        return 4;
      case "high":
        return 3;
      case "moderate":
        return 2;
      case "low":
        return 1;
      default:
        return 1;
    }
  };

  const aiImpact = reportData.deploymentProfile.aiSystemImpact;

  const internalDataValues = [
    getRiskValue(aiImpact.internal.financial.harmRank),
    getRiskValue(aiImpact.internal.reputational.harmRank),
    getRiskValue(aiImpact.internal.operational.harmRank),
    getRiskValue(aiImpact.internal.legal.harmRank),
  ];

  const internalData = {
    labels: ["Financial", "Reputational", "Operational", "Legal"],
    datasets: [
      {
        label: "Internal Risk Impact",
        data: internalDataValues,
        backgroundColor: "rgba(147, 231, 255, 0.5)",
        borderColor: "rgba(77, 221, 255)",
        borderWidth: 2,
        pointBackgroundColor: "rgba(77, 221, 255)",
      },
    ],
  };

  const RENDER_DPR = 3;

  const options: ChartOptions<"radar"> = {
    responsive: false,
    maintainAspectRatio: false,
    animation: false,
    devicePixelRatio: RENDER_DPR,
    layout: {
      padding: 0,
    },
    scales: {
      r: {
        min: 0,
        max: 5,
        ticks: {
          stepSize: 1,
          display: false,
        },
        pointLabels: {
          padding: 2,
          font: {
            size: 10,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        setChartImage(chartRef.current.toBase64Image());
      }
    }, 400);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="radar-chart-wrapper">
      {chartImage ? (
        <img
          src={chartImage}
          alt="Internal Risk Radar"
          className="w-full"
        />
      ) : (
        <Radar
          ref={chartRef}
          data={internalData}
          options={options}
          width={360}
          height={300}
        />
      )}
    </div>
  );
}
